import { FormConfigFile, FormScript, GlobalResponse } from '~/models/Commons ';
import { SET_SCRIPTS } from '~/store/feature/Script';
import { api } from '../api';
import { SCRIPT_PATH, SCRIPT_PATH_LIST } from '../endpoints';

const extendedApiSlice = api.injectEndpoints({
  endpoints: builder => ({
    getScripts: builder.query<
      GlobalResponse<any[]>,
      { page: number; size: number; active: boolean }
    >({
      query: ({ page, size, active }) => ({
        url: `${SCRIPT_PATH_LIST}?page=${page}&size=${size}&&active=${active}`,
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : ``,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(SET_SCRIPTS(data.payload));
        } catch (error) {
          console.error('Error fetching scripts:', error);
        }
      },
    }),
    saveScript: builder.mutation<GlobalResponse<FormScript>, FormScript>({
      query: data => {
        const formData = new FormData();
        const scriptBlob = new Blob(
          [
            JSON.stringify({
              title: data.title,
              description: data.description,
              cloudEnvironmentId:data.cloudEnvironmentId.value
            }),
          ],
          { type: 'application/json' }
        );
        formData.append('script', scriptBlob, 'script.json');

        // Add main script file if available
        if (data.script) {
          formData.append('file', data.script);
        }

        // Handle configFiles based on its type
        if (data.configFiles) {
          if (data.configFiles instanceof File) {
            formData.append('configFiles', data.configFiles);
          } else if (Array.isArray(data.configFiles)) {
            const configBlob = new Blob(
              [JSON.stringify({ configFiles: data.configFiles })],
              { type: 'application/json' }
            );
            formData.append('configFiles', configBlob, 'configFiles.json');
          }
        }

        return {
          url: SCRIPT_PATH,
          method: 'POST',
          body: formData,
          formData: true,
          credentials: 'include',
          headers: {
            Authorization: sessionStorage.getItem('access_token')
              ? `Bearer ${sessionStorage.getItem('access_token')}`
              : '',
          },
        };
      },
    }),

    addConfigFile: builder.mutation<
      GlobalResponse<FormConfigFile>,
      { data: FormConfigFile; deploymentScriptId: string }
    >({
      query: ({ data, deploymentScriptId }) => {
        const formData = new FormData();

        // Handle configFiles based on its type
        if (data.configFiles) {
          if (data.configFiles instanceof File) {
            formData.append('configFiles', data.configFiles);
          } else if (Array.isArray(data.configFiles)) {
            const scriptBlob = new Blob(
              [JSON.stringify({ configFiles: data.configFiles })],
              { type: 'application/json' }
            );
            formData.append('configFiles', scriptBlob, 'configFiles.json');
          }
          formData.append('deploymentScriptId', deploymentScriptId);
        }
        return {
          url: `${SCRIPT_PATH}/${deploymentScriptId}/configFiles`,
          method: 'POST',
          body: formData,
          formData: true,
          credentials: 'include',
          headers: {
            Authorization: sessionStorage.getItem('access_token')
              ? `Bearer ${sessionStorage.getItem('access_token')}`
              : '',
          },
        };
      },
    }),
    deleteScript: builder.mutation<GlobalResponse<FormScript>, string>({
      query: deploymentScriptId  => ({
        url: `${SCRIPT_PATH}/${deploymentScriptId }`,
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: sessionStorage.getItem('access_token')
            ? `Bearer ${sessionStorage.getItem('access_token')}`
            : '',
        },
      }),
    }),
  }),
});

export const {
  useSaveScriptMutation,
  useAddConfigFileMutation,
  useGetScriptsQuery,
  useLazyGetScriptsQuery,
  useDeleteScriptMutation,
} = extendedApiSlice;
